import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import defaultTourCoverPhoto from '../../assets/Images/dafaulttourcoverphoto.png';
import styles from '../ManagerDashboard/Profile.module.css'; // Using the existing styles

const BandToursModal = ({ isOpen, onClose, bandId }) => {
  const [displayedTours, setDisplayedTours] = useState(6);
  const [tours, setTours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && bandId) {
      fetchBandTours();
    }
  }, [isOpen, bandId]);

  const fetchBandTours = async () => {
    if (!bandId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Get all tours for this band
      const toursSnapshot = await getDocs(collection(db, 'band_tours', bandId, 'tours'));
      
      if (!toursSnapshot.empty) {
        const fetchedTours = [];
        
        for (const tourDoc of toursSnapshot.docs) {
          const tourData = tourDoc.data();
          
          // Get verified tour dates
          const tourDatesSnapshot = await getDocs(
            query(
              collection(db, 'band_tours', bandId, 'tours', tourDoc.id, 'tourDates'),
              where('verified', '==', true)
            )
          );
          
          const tourDates = tourDatesSnapshot.docs.map(dateDoc => ({
            id: dateDoc.id,
            ...dateDoc.data()
          }));
          
          // Only include tours with verified dates
          if (tourDates.length > 0) {
            fetchedTours.push({
              id: tourDoc.id,
              tourName: tourData.tourName,
              actName: tourData.actName,
              tourCoverPhotoUrl: tourData.tourCoverPhotoUrl,
              tourDates,
              verifiedDatesCount: tourDates.length
            });
          }
        }
        
        setTours(fetchedTours);
      }
    } catch (error) {
      console.error('Error fetching band tours:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateRange = (tourDates) => {
    if (!tourDates || tourDates.length === 0) return '';
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    
    // Sort dates chronologically
    const sortedDates = [...tourDates].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return `${formatDate(sortedDates[0].date)} - ${formatDate(sortedDates[sortedDates.length - 1].date)}`;
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Tours</h2>
        
        {isLoading ? (
          <div className={styles['loading-indicator']}>
            Loading tour history...
          </div>
        ) : tours.length > 0 ? (
          <>
            <h3 className={styles['section-title']}>Verified Tours</h3>
            <div className={styles['tours-list']}>
              {tours.slice(0, displayedTours).map(tour => (
                <div key={tour.id} className={styles['tour-card']}>
                  <img 
                    src={tour.tourCoverPhotoUrl || defaultTourCoverPhoto} 
                    alt={tour.tourName}
                    className={styles['tour-cover-photo']}
                  />
                  <div className={styles['tour-card-content']}>
                    <h3>{tour.tourName}</h3>
                    <p className={styles['act-name']}>{tour.actName}</p>
                    <p className={styles['tour-dates']}>
                      {formatDateRange(tour.tourDates)}
                    </p>
                    <p className={styles['shows-count']}>
                      {tour.verifiedDatesCount} verified {tour.verifiedDatesCount === 1 ? 'show' : 'shows'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className={styles['no-data-message']}>
            No verified tours available
          </div>
        )}
        
        {displayedTours < tours.length && (
          <button 
            className={styles['load-more-button']}
            onClick={() => setDisplayedTours(prev => prev + 6)}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};

export default BandToursModal;